import { asWidgetProps } from 'Src/types/widget-helpers';
/* eslint-disable clubmed/no-fake-component */
import React, { Fragment, type ComponentProps } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { ArrowButton } from 'Components/frameworks/arrow-button/ArrowButton';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon } from 'Components/frameworks/icon/Icon';
import Image from 'Components/frameworks/image/Image';
import { SliderHandler } from 'Components/frameworks/slider-handler/SliderHandler';
type Props = {
  className?: string;
  items: Array<{
    imageUrl: string;
    title: string;
    description: string;
  }>;
  title: string;
  url?: string;
};
const mainBlockStyles = {
  default: {
    maxHeight: 'min(calc(300rem / 16), 100vw)',
    maxWidth: '320px',
  },
  large: {
    maxHeight: 'min(calc(300rem / 16), 100vw)',
    maxWidth: '700px',
  },
};
const sliderContainerStyles = {
  margin: '0 calc(-1 * ((100vw - min(320px, calc(100vw - 30px))) / 2 + 20px))',
  padding: '0 calc((100vw - min(320px, calc(100vw - 30px))) / 2 + 15px)', // I plead guilty for layout crimes, put me in jail ASAP
};

const getSlideItem = (item, index, hasTwoItems) => (
  <div
    className={classnames('mx-auto px-5 leading-tight text-black md:p-0 lg:w-full', {
      'order-first': hasTwoItems && index === 0,
      'max-w-300 lg:mt-80': hasTwoItems,
      'max-w-360': !hasTwoItems,
    })}
    key={item.title}
  >
    {item.imageUrl && (
      <Image
        {...asWidgetProps<ComponentProps<typeof Image>>({
          className: 'overflow-hidden rounded-12',
          imageUrl: item.imageUrl,
          ratioKey: ratioUtils.XL,
        })}
      />
    )}
    <div className="pt-15 text-16 font-bold">{item.title}</div>
    <div className="pt-10 text-14 leading-normal">{item.description}</div>
  </div>
);

const getSeeMoreButton = (url, hasTwoItems, isTablet, onClick) => {
  const TagName = url ? DynamicLink : 'div';
  const props = url
    ? {
        onClick,
        target: '_blank',
        url,
      }
    : {};
  return (
    <TagName
      {...asWidgetProps<ComponentProps<typeof TagName>>({
        className: classnames('mt-auto flex flex-col items-center text-center text-12', {
          'text-verdigris active:text-verdigris-active hocus:text-verdigris-active':
            (!hasTwoItems && isTablet) || !isTablet,
          'text-ultramarine': hasTwoItems && isTablet,
        }),
        ...props,
      })}
    >
      <Icon
        {...asWidgetProps<ComponentProps<typeof Icon>>({ name: ICONS.NEEDLE, width: '1.875rem' })}
      />
      {!!url && (
        <span className="underline" data-testid="HTCLink">
          {t('common.seeMore')}
        </span>
      )}
    </TagName>
  );
};

const Background = () => (
  <svg
    className="-z-1 shrink-0 overflow-visible lg:mt-50"
    height={460}
    viewBox="0 0 680 680"
    width={460}
  >
    <path
      d="M172 339c0 5-49 14-83 13-35 0-89-10-89-15s53-13 88-13c36 1 84 9 84 15m55 226c14-32 41-72 46-70 4 2-6 50-20 81s-45 76-50 75c-5-2 9-53 24-86Zm-49-32c21-28 56-61 61-58 4 3-18 47-39 74-20 28-60 65-65 62-4-3 21-50 43-78Zm-40-43c27-22 68-46 72-42s-27 42-54 63c-27 22-74 49-78 45-3-4 32-43 60-66Zm-30-51c32-15 78-29 81-24 2 4-37 34-68 49s-84 30-86 26c-2-5 41-35 73-51Zm-16-56c34-8 82-10 84-6v1c1 5-44 25-78 32-33 8-87 10-89 6l-1-1c-1-5 49-24 84-32Zm218 123c5 1 6 50 0 84-7 34-26 86-31 85s-4-54 3-89c6-36 23-81 28-80m40 3c5 0 17 47 18 82 2 34-5 89-10 89s-16-52-18-87c-2-36 4-84 10-84m150-110c2-5 50 3 82 15s79 40 78 45-54-6-87-18c-33-13-74-37-73-42Zm-18 35c3-4 48 14 77 34 28 19 67 57 65 61v1c-3 4-51-19-81-39-29-19-63-52-61-57Zm-26 30c4-4 44 25 67 50 23 26 53 71 50 76-4 3-46-30-70-56s-50-66-47-70Zm-32 23h1c4-3 36 35 53 65 17 29 35 81 31 84-5 3-38-39-55-70-18-31-34-76-30-79Zm-36 15c5-1 28 42 37 76 10 32 16 86 11 89-5 1-28-47-37-81-10-34-15-81-11-84Zm121-141c1-5 50-9 84-4 34 4 87 20 86 25 0 5-53 7-89 3-35-5-82-19-81-24m0-40c0-5 47-19 81-23s89 0 90 5-51 19-86 23c-36 4-84 0-85-5m65-79c32-12 86-20 88-16 2 5-45 30-78 42-34 12-81 20-83 15s40-30 73-41Zm-29-51c29-19 79-40 82-36h1c2 5-37 40-67 59s-74 38-78 34c-3-4 33-39 62-57Zm-40-43c24-25 68-57 72-54 4 4-27 47-51 73-25 25-63 54-68 51-4-4 23-45 47-70Zm-49-33c18-29 53-70 58-69 5 3-15 52-33 83s-49 67-54 65c-5-3 12-49 29-79Zm-55-20c10-33 36-82 41-81 5 2-3 54-14 89-10 33-32 76-37 75s0-50 10-83ZM364 0c5 0 10 53 7 89-2 36-14 82-19 82s-11-49-9-83c2-35 16-88 21-88m-79 4c5-1 22 49 27 85 6 35 6 83 1 84s-23-45-28-79c-6-34-5-89 0-90m-76 22c4-2 32 43 46 76s25 79 20 82c-5 2-32-39-46-71-13-31-25-85-20-87Zm-69 39c4-3 42 34 63 63 20 29 42 72 38 75s-40-30-61-58c-20-27-44-76-40-80Zm-58 54c3-4 48 23 75 47 27 23 58 60 55 64s-46-20-72-42c-27-23-62-66-58-69Zm-44 65c2-4 52 12 84 29 32 16 70 45 68 50-2 4-49-9-80-25s-75-49-72-54Zm-28 74c1-5 54 0 89 9 34 8 78 27 77 32v1c-1 5-50 2-83-6-34-8-83-30-83-36Z"
      fill="#F7F7F7"
    />
  </svg>
);

export const HappyToCare = ({ className, items, title, url }: Props) => {
  const { isTablet, isDesktop } = useResponsive();
  const track = useTracking();

  if (!items?.length) {
    return null;
  }

  const onSeeMoreClick = () => {
    track({
      actionGA: 'happyToCare_Info',
      labelGA: 'Cta_SeeMore_happyToCare',
    });
  };

  const getBottomControls = (
    isPreviousControlDisabled,
    isNextControlDisabled,
    onPrevious,
    onNext,
    index,
  ) => (
    <div className="mt-20 flex items-center justify-center">
      <ArrowButton
        {...asWidgetProps<ComponentProps<typeof ArrowButton>>({
          className: 'me-20 h-12 flex-none text-8',
          color: COLORS.TRANSPARENT,
          isDisabled: isPreviousControlDisabled,
          label: t('common.previous'),
          onClick: onPrevious,
          theme: 'ghost',
        })}
      />

      <span className="flex items-baseline justify-center text-12 text-black">
        <span className="text-14 font-bold">{index + 1}</span>/{items.length}
      </span>
      <ArrowButton
        {...asWidgetProps<ComponentProps<typeof ArrowButton>>({
          className: 'ms-20 h-12 flex-none text-8',
          color: COLORS.TRANSPARENT,
          isDisabled: isNextControlDisabled,
          label: t('common.next'),
          onClick: onNext,
          rotation: 180,
          theme: 'ghost',
        })}
      />
    </div>
  );

  const hasTwoItems = items.length === 2 || isDesktop;
  const mainBlockTheme = !hasTwoItems && isTablet ? 'large' : 'default';
  const containerStyle = {
    ...mainBlockStyles[mainBlockTheme],
    ...((isDesktop && !hasTwoItems) || !isDesktop
      ? {
          maxHeight: 'none',
        }
      : {}),
  };
  return (
    <div className={classnames('relative isolate px-15 lg:flex lg:p-0', className)}>
      <div
        className={classnames(
          'mx-auto flex w-full shrink-0 flex-col items-center rounded-12 p-20 text-ultramarine',
          {
            'lg:mx-20': hasTwoItems,
          },
          {
            'lg:pb-0': !hasTwoItems,
          },
        )}
        style={containerStyle}
      >
        {!isTablet ? (
          <div className="pointer-events-none absolute left-0 right-0 top-0 -mt-60 flex items-center justify-center overflow-hidden">
            <Background />
          </div>
        ) : null}
        <div className="pointer-events-none absolute inset-0 -z-1 mx-15 flex items-center justify-center md:m-0">
          <div
            className="absolute inset-x-0 top-0 mx-auto flex h-full w-full items-center justify-center rounded-12 bg-verdigris"
            style={mainBlockStyles[mainBlockTheme]}
          >
            {isTablet ? <Background /> : null}
          </div>
        </div>
        <Icon
          {...asWidgetProps<ComponentProps<typeof Icon>>({
            className: 'mb-15 shrink-0 lg:my-15',
            name: ICONS.HAPPY_TO_CARE_TEXT,
            width: isTablet ? '6.875rem' : '6.25rem',
          })}
        />

        <div className="mb-20 flex min-h-52 items-center text-center font-brand text-24 leading-tight md:text-28">
          {title}
        </div>

        {!isTablet ? (
          <Fragment>
            <SliderHandler
              {...asWidgetProps<ComponentProps<typeof SliderHandler>>({
                hasKeyboardSupport: true,
                hasSwipeSupport: true,
                initialIndex: 0,
                items: items.map((item, index) => getSlideItem(item, index, hasTwoItems)),
                style: sliderContainerStyles,
              })}
            >
              {({
                hasControls,
                index,
                isNextControlDisabled,
                isPreviousControlDisabled,
                itemsNode,
                onNext,
                onPrevious,
              }) => {
                return (
                  <div className="relative flex flex-1 flex-col pb-20">
                    {itemsNode}
                    {hasControls &&
                      getBottomControls(
                        isPreviousControlDisabled,
                        isNextControlDisabled,
                        onPrevious,
                        onNext,
                        index,
                      )}
                  </div>
                );
              }}
            </SliderHandler>
            {getSeeMoreButton(url, hasTwoItems, isTablet, onSeeMoreClick)}
          </Fragment>
        ) : (
          <Fragment>
            {hasTwoItems && getSeeMoreButton(url, hasTwoItems, isTablet, onSeeMoreClick)}
            {(!isDesktop || !hasTwoItems) && (
              <div
                className={classnames('my-20 flex w-screen', {
                  'justify-between': hasTwoItems,
                  'justify-center': !hasTwoItems,
                })}
              >
                {items.map((item, index) => getSlideItem(item, index, hasTwoItems))}
              </div>
            )}
            {!hasTwoItems && getSeeMoreButton(url, hasTwoItems, isTablet, onSeeMoreClick)}
          </Fragment>
        )}
      </div>
      {isDesktop &&
        hasTwoItems &&
        (items.length > 1
          ? items
          : [
              {
                imageUrl: '',
                title: '',
                description: '',
              },
              ...items,
            ]
        ).map((item, index) => getSlideItem(item, index, hasTwoItems))}
    </div>
  );
};
